import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, filter, map } from 'rxjs';
import axios from 'axios';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import { UNKNOWN_CLIENT_NAME } from 'src/app/shared/payee-cases/types';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { WorkflowService } from 'src/app/shared/services/workflow/workflow.service';
import { environment } from 'src/environments/environment';
import { Client } from 'src/types';
import { User, UsersService } from '../users/users.service';

type UpdateBankAccountParams = {
  label?: string | null;
  account_holder_name?: string;
  account_number?: string;
  iban?: string;
  bic?: string;
  bank_name?: string;
};

type UpdateClientParams = {
  default_tone_of_voice?: 'formal' | 'informal';
};

export type SelectedClient = {
  id: Client['uuid'];
  client_reference_id: string;
  finance_email: string | null;
  bankAccount: {
    uuid: string;
    label: string | null;
    account_holder_name: string | null;
    account_number: string | null;
    bank_name: string | null;
    bic: string | null;
    iban: string | null;
  } | null;
  draft_cases: number;
  ongoing_cases: number;
  closed_cases: number;
  ask_for_tax_number: boolean;
  allow_form: boolean;
  country_iso: string | null;
  partner_integrations: string[];
} & Pick<
  Client,
  'name'
  | 'default_tone_of_voice'
  | 'tax_number'
  | 'uses_credits'
  | 'credits'
>;

type UpdateNotificationSettingsParams = {
  finance_email?: string | null;
};

export type CreateClientParams = {
  client_type: 'org' | 'house';
  name: string;
  party_type_id: string | null;
  tax_number: string | null;
  registration_number: string | null;
  address: {
    country_iso: string;
    postcode: string;
    settlement: string;
    street: string;
  };
  birth_date: string | null;
  birth_place: string | null;
  mother_name: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class ClientsService {
  private authService = inject(AuthService);
  private usersService = inject(UsersService);
  private workflowService = inject(WorkflowService);
  private payeeCasesService = inject(PayeeCasesService);
  private router = inject(Router);
  private snackbar = inject(MatSnackBar);

  private user?: User | null;

  private get selectedClient(): SelectedClient | null { return this.selectedClientSubject.value; };
  get selectedClientId() { return this.selectedClient?.id; }
  get selectedClientIdLocalStoragePath() {
    return this.user ? `selectedClientId/${this.user.email}` : null;
  }

  // tslint:disable-next-line
  private _createClientLoading = false;
  get createClientLoading() { return this._createClientLoading; }

  readonly clientsSubject = new BehaviorSubject<SelectedClient[] | null>(null);
  readonly selectedClientSubject = new BehaviorSubject<SelectedClient | null>(null);

  private clientRefreshGeneration = 0;

  constructor() {
    this.usersService.userSubject.subscribe({
      next: user => this.setUser(user),
    });

    this.selectedClientSubject.subscribe({
      next: () => this.saveSelectedClientIdToLocalStorage(),
    });

    this.workflowService.afterWorkflow.subscribe({
      next: () => this.setClients(),
    });
  }

  setSelectedClient(client?: SelectedClient): void {
    this.selectedClientSubject.next(client ?? null);
  }

  selectClientWithCaseFilter(client: SelectedClient): void {
    this.setSelectedClient(client);
    this.payeeCasesService.client.next({
      name: client.name ?? UNKNOWN_CLIENT_NAME,
      uuid: client.id,
    });
  }

  async getClient(clientId: string): Promise<SelectedClient> {
    return await firstValueFrom(this.clientsSubject.pipe(
      filter((clients): clients is SelectedClient[] => !!clients?.length),
      map(clients => clients.find(c => c.id === clientId)),
      filter((c): c is SelectedClient => !!c),
    ));
  }

  async getSelectedClient(): Promise<SelectedClient> {
    if (this.selectedClient) {
      return this.selectedClient;
    }

    const clients = this.clientsSubject.value;
    if (clients?.length) {
      const client = clients.find(c => c.allow_form) ?? clients[0];
      this.setSelectedClient(client);
      return client;
    }

    return await firstValueFrom(this.selectedClientSubject.pipe(
      filter((v): v is SelectedClient => !!v),
    ));
  }

  async getSelectedFormClient(): Promise<SelectedClient> {
    const selectedClient = await this.getSelectedClient();
    if (selectedClient.allow_form) {
      return selectedClient;
    }

    const formClient = this.clientsSubject.value?.find(
      (client) => client.allow_form,
    );
    if (formClient) {
      this.setSelectedClient(formClient);
      return formClient;
    }

    return selectedClient;
  }

  async updateDefaultToneOfVoice(newToneOfVoice: "formal" | "informal"): Promise<void> {
    const currentClient = this.selectedClient;
    if (currentClient == null) {
      return;
    }

    await this.updateClient({
      default_tone_of_voice: newToneOfVoice,
    });

    const updatedClient: SelectedClient = {
      ...currentClient,
      default_tone_of_voice: newToneOfVoice
    };
    this.selectedClientSubject.next(updatedClient);
  }

  async updateClient(params: UpdateClientParams, clientId = this.selectedClientId): Promise<void> {
    if (!clientId) {
      return;
    }

    const url = `${environment.baseUrl}/api/client/${clientId}`;
    await axios.put(url, params);
  }

  async createClient(params: CreateClientParams): Promise<SelectedClient> {
    const url = `${environment.baseUrl}/api/client`;
    const response = await axios.post<SelectedClient>(url, params);
    this.clientsSubject.next((this.clientsSubject.value ?? []).concat(response.data));
    this.selectedClientSubject.next(response.data);
    return response.data;
  }

  async deleteClientAccess(clientId: string): Promise<void> {
    const url = `${environment.baseUrl}/api/client/${clientId}/access`;
    await axios.delete(url);
    this.clientsSubject.next(this.clientsSubject.value?.filter(c => c.id !== clientId) ?? null);
    if (
      this.selectedClientId === clientId
      && this.clientsSubject.value?.length
    ) {
      this.selectedClientSubject.next(this.clientsSubject.value[0]);
    }
  }

  async updateBankAccount(params: UpdateBankAccountParams): Promise<void> {
    const clientId = this.selectedClientId;
    const url = `${environment.baseUrl}/api/client/${clientId}/bank-account`;
    const refreshGeneration = ++this.clientRefreshGeneration;
    await axios.put(url, params);
    await this.refreshClients(refreshGeneration);
  }

  async updateNotificationSettings(params: UpdateNotificationSettingsParams): Promise<void> {
    if (!this.selectedClientId) {
      return;
    }
    const clientId = this.selectedClientId;
    const url = `${environment.baseUrl}/api/client/${clientId}/notifications`;
    await axios.put(url, params);
    await this.setClients();
  }

  async setClients(): Promise<void> {
    const refreshGeneration = ++this.clientRefreshGeneration;
    await this.refreshClients(refreshGeneration);
  }

  private async refreshClients(refreshGeneration: number): Promise<void> {
    const url = environment.baseUrl + `/api/client`;
    const response = await axios.get<SelectedClient[]>(url);

    if (refreshGeneration !== this.clientRefreshGeneration) {
      return;
    }

    const clients = response.data;
    this.clientsSubject.next(clients);
    if (this.usersService.userSubject.value?.email_verified && clients.length === 0) {
      await this.authService.logout();
      this.snackbar.open(
        'No client found, please contact customer service',
        'OK',
        { duration: 5000 },
      );
      return;
    }

    const selectedClientId = this.getSelectedClientIdFromLocalStorage();
    const cachedClient = clients.find(c => c.id === selectedClientId);
    if (cachedClient) {
      this.setSelectedClient(cachedClient);
    } else if (clients.length > 0) {
      this.setSelectedClient(clients[0]);
    } else if (this.user?.email_verified) {
      this.router.navigateByUrl('user/add-new-client-onboarding');
    }
  }

  private async setUser(user?: User | null): Promise<void> {
    const previousUser = this.user;
    const isNewUser = user?.email !== previousUser?.email;
    const becameVerified = !!user?.email_verified && !previousUser?.email_verified;
    this.user = user;

    if (!user) {
      this.clientsSubject.next(null);
      this.selectedClientSubject.next(null);
      return;
    }

    if (isNewUser || becameVerified) {
      await this.setClients();
    }
  }

  private getSelectedClientIdFromLocalStorage(): string | null {
    if (!this.selectedClientIdLocalStoragePath) {
      return null;
    }
    return localStorage.getItem(this.selectedClientIdLocalStoragePath);
  }

  private saveSelectedClientIdToLocalStorage(id = this.selectedClient?.id): void {
    if (!this.selectedClientIdLocalStoragePath || !id) {
      return;
    }
    localStorage.setItem(this.selectedClientIdLocalStoragePath, id);
  }
}
